import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { PageHeader } from '../components/AppLayout';
import { Avatar } from '../components/Logo';
import { PWAInstallButton } from '../components/PWAInstallButton';
import { PushToggle } from '../components/PushToggle';
import { Users, Bell, LogOut, ChevronRight, Check, Zap, SettingsIcon } from '../components/icons';

export default function MorePage() {
  const { user, logout, isFounder } = useAuth();
  const nav = useNavigate();
  // Iter38 — availability toggle (persisted in db.user_status via /users/{uid}/availability)
  const [available, setAvailable] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    api.get('/users').then((r) => {
      const me = (r.data || []).find((u) => u.id === user.id);
      if (me) setAvailable(me.is_available !== false);
    }).catch(() => {});
  }, [user?.id]);

  const toggle = async () => {
    if (saving) return;
    setSaving(true);
    const next = !available;
    setAvailable(next); // optimistic
    try {
      await api.patch(`/users/${user.id}/availability`, { is_available: next });
      toast.success(next ? 'Tu es marqué disponible' : 'Tu es marqué indisponible');
    } catch (e) {
      setAvailable(!next); // rollback
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader title="Plus" />

      <div style={{ padding: '0 18px 30px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Profile card + quick Réglages entry (Iter 62cn) */}
        <div className="asa-card" style={{ padding: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
          <Avatar user={user} size={54} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t-display-sm" style={{ fontSize: 16 }}>{user?.name}</div>
            <div className="t-body-sm" style={{ color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.role === 'founder' ? 'Founder' : user?.role === 'gestionnaire' ? 'Gestionnaire' : user?.role === 'client' ? 'Client' : 'Editor'} · {user?.email}
            </div>
          </div>
          <button
            data-testid="mobile-profile-settings-btn"
            onClick={() => nav('/reglages')}
            aria-label="Réglages"
            style={{
              padding: '8px 14px',
              borderRadius: 999,
              background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
              color: '#0B1330',
              border: 'none',
              fontFamily: 'Sora, -apple-system, sans-serif',
              fontWeight: 800,
              fontSize: 12,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 4px 12px rgba(62,123,250,0.35)',
              flexShrink: 0,
            }}
          >
            <SettingsIcon size={13} color="#0B1330" strokeWidth={2.4} />
            Réglages
          </button>
        </div>

        {/* Iter38 — availability toggle */}
        <Section title="Disponibilité">
          <button
            data-testid="availability-toggle"
            onClick={toggle}
            disabled={saving}
            style={{
              display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', width: '100%',
              textAlign: 'left', transition: 'background 0.12s ease',
              background: 'transparent',
            }}
          >
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: available ? 'rgba(52,211,153,0.18)' : 'rgba(255,107,107,0.18)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: available ? '#34D399' : '#FF6B6B',
                boxShadow: available ? '0 0 8px #34D399' : 'none',
              }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 14, color: 'var(--t1)' }}>
                {available ? 'Disponible' : 'Indisponible'}
              </div>
              <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)', marginTop: 2 }}>
                {available
                  ? 'L\'équipe peut te confier des nouveaux projets'
                  : 'Pas de nouvelles missions tant que tu n\'es pas dispo'}
              </div>
            </div>
            {/* Switch UI */}
            <div data-testid="availability-switch" style={{
              width: 42, height: 24, borderRadius: 999,
              background: available ? '#34D399' : 'var(--sf2)',
              border: '1.5px solid ' + (available ? '#34D399' : 'var(--bd)'),
              position: 'relative', transition: 'background 0.2s, border 0.2s',
              flexShrink: 0,
            }}>
              <div style={{
                position: 'absolute', top: 2, left: available ? 20 : 2,
                width: 18, height: 18, borderRadius: '50%',
                background: 'white', transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              }} />
            </div>
          </button>
        </Section>

        {/* Team section */}
        <Section title="Équipe">
          <Row icon={Users} label="Charge de l'équipe" accent="#5BBEFC" onClick={() => nav('/team')} />
          <Row icon={Users} label="Messagerie" accent="#A78BFA" onClick={() => nav('/messages')} />
        </Section>

        {/* Iter 62ac — Écosystème Elevate Influence (founders only) */}
        {isFounder && (
          <Section title="Écosystème Elevate Influence">
            {/* Iter 62bg — CTA direct pour se connecter à l'app Elevate déployée */}
            <a
              href="https://elevate-workspace-1.emergent.host"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="mobile-elevate-direct-cta"
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '16px 16px', margin: '0',
                textDecoration: 'none',
                background:
                  'radial-gradient(120% 100% at 100% 0%, rgba(91,190,252,0.22) 0%, rgba(91,190,252,0) 55%),'
                  + ' linear-gradient(160deg, rgba(30,40,84,0.75) 0%, rgba(14,20,50,0.85) 100%)',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              <img
                src="/elevate-logo.png"
                alt=""
                width={36} height={36}
                style={{
                  borderRadius: 10, flexShrink: 0,
                  boxShadow: '0 0 0 1px rgba(91,190,252,0.45), 0 8px 22px -10px rgba(91,190,252,0.55)',
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 9.5,
                  color: '#5BBEFC', letterSpacing: '0.14em', textTransform: 'uppercase',
                }}>Se connecter en direct</div>
                <div style={{
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 15,
                  color: 'var(--t1)', marginTop: 3,
                }}>Ouvrir Elevate Influence School</div>
              </div>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '8px 12px', borderRadius: 999,
                background: 'linear-gradient(135deg, #5BBEFC 0%, #3E7BFA 100%)',
                color: '#0B1330',
                fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
                flexShrink: 0,
              }}>Ouvrir ↗</span>
            </a>
            <ExternalRow
              logo="/elevate-logo.png"
              label="Sales Elevate"
              sub="Suivi sales & SIM"
              href="https://studio-ops-dash.emergent.host"
              testId="mobile-nav-sales-elevate"
            />
            <ExternalRow
              logo="/elevate-logo.png"
              label="Elevate School (vue ASA)"
              sub="Calendrier des tâches, cash, retards"
              to="/elevate-school"
              testId="mobile-nav-elevate-school"
            />
          </Section>
        )}

        {/* Iter 62ch — Réglages entry (personal settings page) */}
        <Section title="Personnalisation">
          <ExternalRow
            logo={<SettingsIcon size={18} color="#5BBEFC" strokeWidth={2} />}
            label="Réglages"
            sub="Calendly, notifs, préférences perso"
            to="/reglages"
            testId="mobile-nav-settings"
          />
        </Section>

        <Section title="Application">
          {/* Iter 62bf — expose push notif toggle + PWA install directly on Profil */}
          <div style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: 'rgba(245,166,35,0.24)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <Bell size={15} color="#F5A623" strokeWidth={1.8} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Lexend', fontSize: 15, color: 'var(--text-primary)' }}>Notifications push</div>
              <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>
                Active pour recevoir les alertes en temps réel
              </div>
            </div>
            <div data-testid="more-push-toggle"><PushToggle compact /></div>
          </div>
          <div style={{
            padding: '14px 16px',
            display: 'flex', alignItems: 'center', gap: 14,
            borderBottom: '1px solid var(--border-subtle)',
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: 'rgba(91,190,252,0.24)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <Zap size={15} color="#5BBEFC" strokeWidth={1.8} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Lexend', fontSize: 15, color: 'var(--text-primary)' }}>Widget iPhone</div>
              <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>
                Épingle le calendrier sur ton écran d&apos;accueil
              </div>
            </div>
            <div data-testid="more-pwa-install"><PWAInstallButton label="Épingler" target="/widget/calendar" /></div>
          </div>
          {/* Iter 62bl — Test push button (send a fake notif to your own devices to verify) */}
          <button
            data-testid="more-push-test-btn"
            onClick={async () => {
              try {
                await api.post('/push/test');
                toast.success('Push test envoyé · check ta cloche et ton iPhone');
                if (navigator.vibrate) navigator.vibrate(15);
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Erreur push test');
              }
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px', width: '100%',
              textAlign: 'left', background: 'transparent',
              border: 'none', cursor: 'pointer',
              transition: 'background 0.1s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface2)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{
              width: 34, height: 34, borderRadius: 8,
              background: 'rgba(167,139,250,0.24)',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <Bell size={15} color="#A78BFA" strokeWidth={1.8} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: 'Lexend', fontSize: 15, color: 'var(--text-primary)' }}>Envoyer un push test</div>
              <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--text-dim)', marginTop: 2 }}>
                Vérifie que ton iPhone reçoit bien les notifs
              </div>
            </div>
            <span style={{
              padding: '6px 12px', borderRadius: 999,
              background: 'linear-gradient(135deg, #A78BFA 0%, #5BBEFC 100%)',
              color: '#0B1330',
              fontFamily: 'Sora', fontWeight: 800, fontSize: 11,
              flexShrink: 0,
            }}>Envoyer</span>
          </button>
        </Section>

        <Section>
          <Row
            icon={LogOut}
            label="Déconnexion"
            accent="#FF6B6B"
            danger
            onClick={() => { if (window.confirm('Se déconnecter ?')) logout(); }}
            testId="logout-btn"
          />
        </Section>

        <div style={{ textAlign: 'center', color: 'var(--text-dim)', fontFamily: 'Lexend', fontSize: 11, marginTop: 8 }}>
          ASA v1.0 · Elevate Influence — Maison mère
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      {title && <div className="asa-eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>{title}</div>}
      <div className="asa-card" style={{ overflow: 'hidden' }}>{children}</div>
    </div>
  );
}

function Row({ icon: Icon, label, accent, onClick, danger, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', width: '100%',
        textAlign: 'left', borderBottom: '1px solid var(--border-subtle)',
        transition: 'background 0.1s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <div style={{
        width: 34, height: 34, borderRadius: 8,
        background: `${accent}24`, display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <Icon size={15} color={accent} strokeWidth={1.8} />
      </div>
      <span style={{ flex: 1, fontFamily: 'Lexend', fontSize: 15, color: danger ? 'var(--red)' : 'var(--text-primary)' }}>{label}</span>
      <ChevronRight size={14} color="var(--text-dim)" />
    </button>
  );
}

// Iter 62ac — External SaaS row (Sales Elevate) or internal (Elevate School native)
function ExternalRow({ logo, label, sub, href, to, testId }) {
  const nav = useNavigate();
  const Tag = to ? 'button' : 'a';
  const props = to
    ? { onClick: () => nav(to), type: 'button' }
    : { href, target: '_blank', rel: 'noreferrer noopener' };
  return (
    <Tag
      {...props}
      data-testid={testId}
      style={{
        display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', width: '100%',
        textAlign: 'left', borderBottom: '1px solid var(--border-subtle)',
        textDecoration: 'none', background: 'transparent', border: 'none',
        cursor: 'pointer',
        transition: 'background 0.1s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface2)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      <img
        src={logo}
        alt=""
        width={34}
        height={34}
        style={{
          borderRadius: 8,
          objectFit: 'cover',
          flexShrink: 0,
          boxShadow: '0 2px 8px rgba(46,163,242,0.35)',
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Lexend', fontSize: 15, color: 'var(--text-primary)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{label}</div>
        <div style={{
          fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--text-dim)', marginTop: 1,
        }}>{sub}</div>
      </div>
      <ChevronRight size={14} color="var(--text-dim)" />
    </Tag>
  );
}
